import { useMemo } from 'react';
import { IconButton, Tooltip } from '@mui/material';
import { RecordVoiceOver, VoiceOverOff } from '@mui/icons-material';
import { useTranslations } from '@/i18n/use-translations';
import { useUpdateLiveContextMutation } from '@/state/mutations/player/use-update-live-context';
import type { LiveContextStateDto } from '@/state/queries/player/use-get-live-context-state';
import { useGetPlayersQuery } from '@/state/queries/players/use-get-players';
import { useLiveContextCueState } from '@/hooks/use-live-context-cue-state';
import { useLiveContextStateWithLivePlayState } from '@/hooks/use-live-play-state';
import { resolveLiveBindingFlowId } from '@/helpers/object.id.helper';
import { remoteSpeakersForSection } from '@/helpers/remote-speaker-players.helper';
import type { FlowDto } from '@/types/dtos/flows/response/flow.dto';
import { performEqualSpreadIconButtonSx } from './perform-equal-spread-icon-row';

/**
 * Lower controls: bring every connected remote speaker of the current section on stage, or take them
 * off. A section advance or a cue jump turns it off on the server.
 */
export function RemoteSpeakersOnStageToggle({
  flow,
  entityRef,
  eventSlotId,
  liveContextState,
}: {
  flow: FlowDto;
  entityRef: string;
  eventSlotId?: string;
  liveContextState?: LiveContextStateDto;
}) {
  const t = useTranslations();
  const updateLiveContext = useUpdateLiveContextMutation();
  const bindingFlowId = resolveLiveBindingFlowId(flow, eventSlotId);
  const liveReady = flow?.liveContext?.isRunning ?? false;
  const { data: players = [] } = useGetPlayersQuery(
    bindingFlowId
      ? {
          entityId: bindingFlowId,
          entityType: 'Flow',
          ...(eventSlotId ? { eventSlotId } : {}),
        }
      : undefined,
    { enabled: !!bindingFlowId && liveReady },
  );
  const livePicture = useLiveContextStateWithLivePlayState(entityRef, liveContextState);
  const cueState = useLiveContextCueState(entityRef, undefined, liveContextState?.cueState);
  const speakerCount = useMemo(
    () => remoteSpeakersForSection(players, cueState?.activeSlotId).length,
    [players, cueState?.activeSlotId],
  );

  const onStage = livePicture?.remoteSpeakersOnStage === true;
  const [entityType, entityId] = entityRef.split(':') as ['Flow' | 'Event', string];
  const disabled = !entityId || (!onStage && speakerCount === 0);
  const label = onStage
    ? t('flows.detail.perform.remoteSpeakersOnStage.takeOff')
    : speakerCount === 0
      ? t('flows.detail.perform.remoteSpeakersOnStage.none')
      : t('flows.detail.perform.remoteSpeakersOnStage.bringOn', { count: speakerCount });

  return (
    <Tooltip title={label}>
      <span>
        <IconButton
          size="medium"
          aria-label={label}
          aria-pressed={onStage}
          disabled={disabled}
          onClick={() => updateLiveContext.mutate({ entityType, entityId, remoteSpeakersOnStage: !onStage })}
          sx={{
            ...performEqualSpreadIconButtonSx,
            color: onStage ? 'error.main' : undefined,
          }}
        >
          {onStage ? <RecordVoiceOver /> : <VoiceOverOff />}
        </IconButton>
      </span>
    </Tooltip>
  );
}
